/** GENERATED_ASSET — assembles layers, anchors, protected regions, bounds and hashes. */
import type {
  FountainArtifact, LayerArtifact, LayerId, PaletteRole, ProtectedVisualRegion, ResolvedPalette, VisualAnchor, VisualBounds,
} from './types';
import { LAYER_ORDER } from './types';
import { piecePalette } from '../../../../../shared/assets/pieces/palettes';
import { PALETTES, STYLE, TOKEN_REVISION, parseHex } from './palette';
import { makeCtx } from './ctx';
import type { Ctx } from './ctx';
import { validateRecipe } from './validate';
import { Bounds } from './pathKit';
import { Layer } from './svgKit';
import { CX, FRAME, GROUND, waterCut } from './layout';
import { paintBasin, paintContactShadow } from './basin';
import { paintStele, paintTiers } from './tiers';
import { paintSpout, spoutMouth } from './brass';
import { paintCracks, paintGrain, paintMoss, paintSalt } from './wear';
import { paintWater } from './water';
import { landingPoint, paintFoam, paintSpray } from './foam';
import { sha256, stableStringify } from './rng';

export const GENERATOR_ID = 'scenery.tidal_cascade_fountain.v1';
export const GENERATOR_REVISION = 'scenery.tidal_cascade_fountain.v1/candidate-1';
export const ASSET_ID = 'prop.coastal.tidal_cascade_fountain';

export interface GenerateOptions {
  /** Serialization-only id scope. Never changes geometry, seeds or anchors. */
  namespace?: string;
  /** Optional palette override (all 22 roles, #RRGGBB). */
  palette?: ResolvedPalette;
  /** Optional 0..1 water-motion phase for the finite motion preview; omitted = static art. */
  phase?: number;
}

const ROLES = Object.keys(PALETTES.coastal_salt) as PaletteRole[];
const NS_RE = /^[A-Za-z][A-Za-z0-9_-]{0,63}$/;

function checkPalette(p: ResolvedPalette): void {
  const problems: string[] = [];
  for (const r of ROLES) {
    try { parseHex(p[r]); } catch { problems.push(`${r}=${JSON.stringify(p[r])}`); }
  }
  if (problems.length) throw new Error(`Invalid palette override; every role needs #RRGGBB: ${problems.join(', ')}`);
}

function paintLayers(ctx: Ctx): Record<LayerId, Layer> {
  const layers = Object.fromEntries(LAYER_ORDER.map((id) => [id, new Layer(id, ctx.ns)])) as Record<LayerId, Layer>;
  const b = layers.body;
  paintContactShadow(ctx, b);
  paintBasin(ctx, b);
  paintStele(ctx, b);
  paintSpout(ctx, b);
  paintTiers(ctx, b);
  paintSalt(ctx, b);
  paintMoss(ctx, b);
  paintCracks(ctx, b);
  paintGrain(ctx, b);
  paintWater(ctx, layers.water);
  paintFoam(ctx, layers.foam);
  paintSpray(ctx, layers.spray);
  return layers;
}

const r3 = (v: number) => Math.round(v * 1000) / 1000;
function rect(x0: number, y0: number, x1: number, y1: number): VisualBounds {
  return { x: r3(x0), y: r3(y0), width: r3(x1 - x0), height: r3(y1 - y0) };
}

function buildAnchors(ctx: Ctx): VisualAnchor[] {
  const { L, p } = ctx;
  const anchors: VisualAnchor[] = [
    { name: 'origin', point: { x: CX, y: FRAME.h }, role: 'origin' },
    { name: 'base_contact', point: { x: CX, y: GROUND }, role: 'support' },
    { name: 'base_contact_left', point: { x: L.basin.x0 + 4, y: GROUND }, role: 'support' },
    { name: 'base_contact_right', point: { x: L.basin.x1 - 4, y: GROUND }, role: 'support' },
    { name: 'water_source', point: spoutMouth(ctx), role: 'effect' },
  ];
  // spill_lip_1 is the TOP lip; numbering grows downhill toward the basin.
  for (let n = 1; n <= p.tiers; n++) {
    const t = L.tiers[p.tiers - n]!;
    anchors.push({ name: `spill_lip_${n}`, point: { x: t.lipX, y: t.s + t.notchDepth }, role: 'effect' });
  }
  const cut = waterCut(L);
  anchors.push({ name: 'basin_surface', point: { x: CX, y: r3((cut.y + (L.water.present ? L.water.y1 : L.basin.openY1)) / 2) }, role: 'effect' });
  anchors.push({ name: 'splash_center', point: landingPoint(L.tiers[0]!), role: 'effect' });
  return anchors;
}

function buildRegions(ctx: Ctx): ProtectedVisualRegion[] {
  const { L, p } = ctx;
  const b = L.basin;
  const regions: ProtectedVisualRegion[] = [
    { name: 'water_opening', role: 'opening', bounds: rect(b.openX0, b.openY0, b.openX1, b.openY1), gameplayAuthority: false },
  ];
  for (let n = 1; n <= p.tiers; n++) {
    const t = L.tiers[p.tiers - n]!;
    regions.push({
      name: `spill_lane_${n}`, role: 'clear-lane', gameplayAuthority: false,
      bounds: rect(t.lipX - t.notchW / 2, t.s, t.lipX + t.notchW / 2, t.base + 3),
    });
  }
  return regions;
}

function layerArtifact(id: LayerId, layer: Layer, svg: string): LayerArtifact {
  return { id, svg, svgBytesHash: sha256(svg), empty: layer.isEmpty, paintBounds: layer.paint.toRect(), contentBounds: layer.content.toRect() };
}

function esc(s: string): string { return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;'); }

export function generateFountain(input: unknown, opts: GenerateOptions = {}): FountainArtifact {
  const recipe = validateRecipe(input);
  const palette = opts.palette ?? piecePalette(recipe.paletteId);
  if (opts.palette) checkPalette(opts.palette);
  if (opts.phase !== undefined && !(opts.phase >= 0 && opts.phase <= 1)) throw new Error(`phase must be within [0, 1] (got ${opts.phase})`);
  const logicalRecipeHash = sha256(stableStringify({
    recipe, generatorRevision: GENERATOR_REVISION, tokenRevision: TOKEN_REVISION, style: STYLE, palette,
  }));
  const ns = opts.namespace ?? `tcf-${logicalRecipeHash.slice(0, 10)}`;
  if (!NS_RE.test(ns)) throw new Error(`Invalid SVG namespace ${JSON.stringify(ns)}: use a letter then letters, digits, "-" or "_" (max 64)`);
  const ctx = makeCtx(recipe, palette, ns, opts.phase);
  const layers = paintLayers(ctx);

  const common = `xmlns="http://www.w3.org/2000/svg" width="${FRAME.w}" height="${FRAME.h}" viewBox="0 0 ${FRAME.w} ${FRAME.h}"`;
  const idAttrs = `data-asset-id="${ASSET_ID}" data-generator-id="${GENERATOR_ID}" data-recipe-id="${esc(recipe.id)}" data-integration="GENERATED_ASSET" data-origin="${CX},${FRAME.h}"`;
  const title = `<title>Tidewend Cascade Fountain (${recipe.params.variant}) - GENERATED_ASSET</title>`;
  const layerSvgs = {} as Record<LayerId, LayerArtifact>;
  for (const id of LAYER_ORDER) {
    const l = layers[id];
    const svg = `<svg ${common} ${idAttrs} data-layer="${id}">${title}${l.defsMarkup()}${l.groupMarkup()}</svg>`;
    layerSvgs[id] = layerArtifact(id, l, svg);
  }
  const defs = LAYER_ORDER.map((id) => layers[id].defsMarkup().replace(/^<defs>|<\/defs>$/g, '')).join('');
  const body = LAYER_ORDER.filter((id) => !layers[id].isEmpty).map((id) => layers[id].groupMarkup()).join('');
  const svg = `<svg ${common} ${idAttrs}>${title}${defs ? `<defs>${defs}</defs>` : ''}${body}</svg>`;

  const paint = new Bounds(), content = new Bounds();
  for (const id of LAYER_ORDER) { paint.merge(layers[id].paint); content.merge(layers[id].content); }
  const display = {
    designFrame: { width: 384 as const, height: 320 as const }, proposedScale: 0.5,
    displayFrame: { width: 192, height: 160 },
    note: 'Design units are 2x the proposed world frame; final scale needs in-game review against the 40x40 player hitbox.',
  };
  const anchors = buildAnchors(ctx);
  return {
    kind: 'svg-artifact', integration: 'GENERATED_ASSET',
    assetId: ASSET_ID, recipeId: recipe.id, generatorId: GENERATOR_ID, generatorRevision: GENERATOR_REVISION, logicalRecipeHash,
    svg, svgBytesHash: sha256(svg),
    origin: { kind: 'bottom-center', point: { x: CX, y: FRAME.h } },
    contentBounds: content.toRect(), paintBounds: paint.toRect(), viewBox: { x: 0, y: 0, width: FRAME.w, height: FRAME.h },
    anchors, protectedRegions: buildRegions(ctx), renderRole: 'back-structure', gameplayGeometry: 'not-generated',
    layers: layerSvgs,
    support: {
      kind: 'painted-base-contact', point: { x: CX, y: GROUND },
      span: { x0: ctx.L.basin.x0 + 4, x1: ctx.L.basin.x1 - 4, y: GROUND }, physicalClaim: 'none',
    },
    display, namespace: ns,
    logicalSource: { kind: 'recipe', recipeId: recipe.id, assetId: ASSET_ID },
  };
}
